import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { User, LogOut, Bell, Heart, ChevronRight, XCircle, LayoutGrid, Car, Calendar, Clipboard, DollarSign, MessageSquare } from "lucide-react";
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { useFavorites } from "@/hooks/use-favorites";
import { getDisplayName } from "@/lib/utils";
import { supabase } from "@/lib/supabase";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { getAdminHomePath, readAdminScopeFromUser } from "@/lib/admin/adminScope";

interface UserMenuContentProps {
  user: any;
  onSignOut: () => void;
  getInitials: (user: any) => string;
  unreadCount?: number;
}

export const UserMenuContent = ({
  user,
  onSignOut,
  getInitials: getInitialsProp,
  unreadCount = 0,
}: UserMenuContentProps) => {
  const { t } = useTranslation();
  const { getUserRole } = useAuth();
  const [userRole, setUserRole] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [profile, setProfile] = useState<{
    role?: string;
    agency_name?: string | null;
    first_name?: string | null;
    last_name?: string | null;
  } | null>(null);
  const { favorites } = useFavorites();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const role = await getUserRole();
        setUserRole(role);

        // Récupérer le profil complet depuis la base de données pour avoir agency_name
        if (user?.id) {
          const { data: profileData, error } = await supabase
            .from("profiles")
            .select("role, agency_name, first_name, last_name")
            .eq("id", user.id)
            .single();

          if (!error && profileData) {
            setProfile(profileData);
          }
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des données utilisateur:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [getUserRole, user?.id]);

  const isOwner = userRole === "owner";
  const isRenter = userRole === "renter";
  const isAdmin = userRole === "admin";

  const getRoleBadgeColor = () => {
    if (isOwner) return "bg-emerald-subtle text-green-night border border-green-night/20";
    if (isRenter) return "bg-emerald-soft text-green-night border border-green-night/20";
    if (isAdmin) return "bg-scandi-surfaceAlt text-text-primary border border-border";
    return "bg-scandi-surfaceAlt text-text-secondary border border-border";
  };

  return (
    <>
      {/* Header Premium avec Avatar et Infos - Style scandinave */}
      <DropdownMenuLabel className="font-normal p-0 mb-2">
        <div className="flex items-center gap-3 p-3 rounded-xl bg-emerald-soft/50 border border-emerald-subtle/50">
          <Avatar className="h-12 w-12 border-2 border-white shadow-md ring-2 ring-green-night/10">
            <AvatarImage
              src={user.user_metadata?.avatar_url}
              alt={profile ? getDisplayName(profile) : user.email?.split("@")[0]}
              className="object-cover"
            />
            <AvatarFallback className="bg-gradient-to-br from-green-night to-green-light text-white font-semibold text-base">
              {getInitialsProp(user)}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col space-y-1 flex-1 min-w-0">
            <p className="text-sm font-semibold leading-tight text-text-primary truncate">
            {profile
              ? getDisplayName(profile)
              : user.user_metadata?.first_name && user.user_metadata?.last_name
                ? `${user.user_metadata.first_name} ${user.user_metadata.last_name}`
                : user.email?.split("@")[0] || t('common.user')}
          </p>
            <p className="text-xs leading-tight text-text-tertiary truncate">{user.email}</p>
          {isLoading ? (
              <div className="h-4 w-20 bg-scandi-surfaceAlt rounded animate-pulse mt-1" />
          ) : (
              <Badge className={cn("text-xs w-fit mt-1 shadow-sm", getRoleBadgeColor())}>
                {userRole === "owner"
                  ? t('common.owner')
                  : userRole === "renter"
                    ? t('common.renter')
                    : userRole === "admin"
                      ? t('common.admin')
                      : t('common.user')}
              </Badge>
            )}
            </div>
        </div>
      </DropdownMenuLabel>
      
      <DropdownMenuSeparator className="my-2 bg-gradient-to-r from-transparent via-border to-transparent" />

      {/* Quick access by role - nav links from navbar */}
      <div className="space-y-1">
        {isOwner && (
          <>
            <DropdownMenuItem asChild>
              <Link to="/dashboard/owner" className="cursor-pointer group flex items-center justify-between rounded-lg px-3 py-2.5 transition-all duration-200 hover:bg-emerald-soft/50 hover:translate-x-1">
                <div className="flex items-center gap-3">
                  <div className="p-1.5 rounded-lg bg-emerald-soft/50 group-hover:bg-emerald-subtle transition-colors duration-200">
                    <LayoutGrid className="h-4 w-4 text-green-night" />
                  </div>
                  <span className="font-medium text-sm text-text-primary">{t('nav.dashboard')}</span>
                </div>
                <ChevronRight className="h-4 w-4 text-text-tertiary opacity-0 group-hover:opacity-100" />
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/dashboard/owner/vehicles" className="cursor-pointer group flex items-center justify-between rounded-lg px-3 py-2.5 transition-all duration-200 hover:bg-emerald-soft/50 hover:translate-x-1">
                <div className="flex items-center gap-3">
                  <div className="p-1.5 rounded-lg bg-emerald-soft/50 group-hover:bg-emerald-subtle transition-colors duration-200">
                    <Car className="h-4 w-4 text-green-night" />
                  </div>
                  <span className="font-medium text-sm text-text-primary">{t('nav.myVehicles')}</span>
                </div>
                <ChevronRight className="h-4 w-4 text-text-tertiary opacity-0 group-hover:opacity-100" />
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/dashboard/owner/fleet" className="cursor-pointer group flex items-center justify-between rounded-lg px-3 py-2.5 transition-all duration-200 hover:bg-emerald-soft/50 hover:translate-x-1">
                <div className="flex items-center gap-3">
                  <div className="p-1.5 rounded-lg bg-emerald-soft/50 group-hover:bg-emerald-subtle transition-colors duration-200">
                    <Clipboard className="h-4 w-4 text-green-night" />
                  </div>
                  <span className="font-medium text-sm text-text-primary">{t('nav.fleetAvailability')}</span>
                </div>
                <ChevronRight className="h-4 w-4 text-text-tertiary opacity-0 group-hover:opacity-100" />
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/dashboard/owner/bookings" className="cursor-pointer group flex items-center justify-between rounded-lg px-3 py-2.5 transition-all duration-200 hover:bg-emerald-soft/50 hover:translate-x-1">
                <div className="flex items-center gap-3">
                  <div className="p-1.5 rounded-lg bg-emerald-soft/50 group-hover:bg-emerald-subtle transition-colors duration-200">
                    <Calendar className="h-4 w-4 text-green-night" />
                  </div>
                  <span className="font-medium text-sm text-text-primary">{t('nav.bookings')}</span>
                </div>
                <ChevronRight className="h-4 w-4 text-text-tertiary opacity-0 group-hover:opacity-100" />
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/dashboard/owner/revenue" className="cursor-pointer group flex items-center justify-between rounded-lg px-3 py-2.5 transition-all duration-200 hover:bg-emerald-soft/50 hover:translate-x-1">
                <div className="flex items-center gap-3">
                  <div className="p-1.5 rounded-lg bg-emerald-soft/50 group-hover:bg-emerald-subtle transition-colors duration-200">
                    <DollarSign className="h-4 w-4 text-green-night" />
                  </div>
                  <span className="font-medium text-sm text-text-primary">{t('nav.revenue')}</span>
                </div>
                <ChevronRight className="h-4 w-4 text-text-tertiary opacity-0 group-hover:opacity-100" />
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/messages" className="cursor-pointer group flex items-center justify-between rounded-lg px-3 py-2.5 transition-all duration-200 hover:bg-emerald-soft/50 hover:translate-x-1">
                <div className="flex items-center gap-3">
                  <div className="p-1.5 rounded-lg bg-emerald-soft/50 group-hover:bg-emerald-subtle transition-colors duration-200">
                    <MessageSquare className="h-4 w-4 text-green-night" />
                  </div>
                  <span className="font-medium text-sm text-text-primary">{t('nav.messages')}</span>
                </div>
                <ChevronRight className="h-4 w-4 text-text-tertiary opacity-0 group-hover:opacity-100" />
              </Link>
            </DropdownMenuItem>
          </>
        )}
        {isRenter && (
          <>
            <DropdownMenuItem asChild>
              <Link to="/dashboard/renter" className="cursor-pointer group flex items-center justify-between rounded-lg px-3 py-2.5 transition-all duration-200 hover:bg-emerald-soft/50 hover:translate-x-1">
                <div className="flex items-center gap-3">
                  <div className="p-1.5 rounded-lg bg-emerald-soft/50 group-hover:bg-emerald-subtle transition-colors duration-200">
                    <LayoutGrid className="h-4 w-4 text-green-night" />
                  </div>
                  <span className="font-medium text-sm text-text-primary">{t('nav.dashboard')}</span>
                </div>
                <ChevronRight className="h-4 w-4 text-text-tertiary opacity-0 group-hover:opacity-100" />
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/dashboard/renter/bookings" className="cursor-pointer group flex items-center justify-between rounded-lg px-3 py-2.5 transition-all duration-200 hover:bg-emerald-soft/50 hover:translate-x-1">
                <div className="flex items-center gap-3">
                  <div className="p-1.5 rounded-lg bg-emerald-soft/50 group-hover:bg-emerald-subtle transition-colors duration-200">
                    <Calendar className="h-4 w-4 text-green-night" />
                  </div>
                  <span className="font-medium text-sm text-text-primary">{t('nav.myBookings')}</span>
                </div>
                <ChevronRight className="h-4 w-4 text-text-tertiary opacity-0 group-hover:opacity-100" />
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/messages" className="cursor-pointer group flex items-center justify-between rounded-lg px-3 py-2.5 transition-all duration-200 hover:bg-emerald-soft/50 hover:translate-x-1">
                <div className="flex items-center gap-3">
                  <div className="p-1.5 rounded-lg bg-emerald-soft/50 group-hover:bg-emerald-subtle transition-colors duration-200">
                    <MessageSquare className="h-4 w-4 text-green-night" />
                  </div>
                  <span className="font-medium text-sm text-text-primary">{t('nav.messages')}</span>
                </div>
                <ChevronRight className="h-4 w-4 text-text-tertiary opacity-0 group-hover:opacity-100" />
              </Link>
            </DropdownMenuItem>
          </>
        )}
        {isAdmin && (
          <DropdownMenuItem asChild>
            <Link to={getAdminHomePath(readAdminScopeFromUser(user))} className="cursor-pointer group flex items-center justify-between rounded-lg px-3 py-2.5 transition-all duration-200 hover:bg-emerald-soft/50 hover:translate-x-1">
              <div className="flex items-center gap-3">
                <div className="p-1.5 rounded-lg bg-emerald-soft/50 group-hover:bg-emerald-subtle transition-colors duration-200">
                  <LayoutGrid className="h-4 w-4 text-green-night" />
                </div>
                <span className="font-medium text-sm text-text-primary">
                  {readAdminScopeFromUser(user) === "va" ? t('nav.agencies') : t('nav.dashboard')}
                </span>
              </div>
              <ChevronRight className="h-4 w-4 text-text-tertiary opacity-0 group-hover:opacity-100" />
            </Link>
          </DropdownMenuItem>
        )}
      </div>

      <DropdownMenuSeparator className="my-2 bg-gradient-to-r from-transparent via-border to-transparent" />

      {/* Menu Items avec animations - Style scandinave */}
      <div className="space-y-1">
      <DropdownMenuItem asChild>
          <Link 
            to="/profile" 
            className="cursor-pointer group flex items-center justify-between rounded-lg px-3 py-2.5 transition-all duration-200 hover:bg-emerald-soft/50 hover:translate-x-1"
          >
            <div className="flex items-center gap-3">
              <div className="p-1.5 rounded-lg bg-emerald-soft/50 group-hover:bg-emerald-subtle transition-colors duration-200">
                <User className="h-4 w-4 text-green-night group-hover:scale-110 transition-transform duration-200" />
              </div>
              <span className="font-medium text-sm text-text-primary">{t('nav.myProfile')}</span>
            </div>
            <ChevronRight className="h-4 w-4 text-text-tertiary opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
        </Link>
      </DropdownMenuItem>

      <DropdownMenuItem asChild>
          <Link 
            to="/favorites" 
            className="cursor-pointer group flex items-center justify-between rounded-lg px-3 py-2.5 transition-all duration-200 hover:bg-emerald-soft/50 hover:translate-x-1"
          >
            <div className="flex items-center gap-3 flex-1">
              <div className="p-1.5 rounded-lg bg-emerald-soft/50 group-hover:bg-emerald-subtle transition-colors duration-200">
                <Heart className="h-4 w-4 text-green-night group-hover:scale-110 transition-transform duration-200" />
              </div>
              <span className="font-medium text-sm text-text-primary">{t('nav.favorites')}</span>
          </div>
            <div className="flex items-center gap-2">
          {favorites.length > 0 && (
                <Badge className="bg-green-night text-white text-xs px-2 py-0.5 shadow-sm">
                  {favorites.length}
                </Badge>
          )}
              <ChevronRight className="h-4 w-4 text-text-tertiary opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
            </div>
        </Link>
      </DropdownMenuItem>

      <DropdownMenuItem asChild>
          <Link 
            to="/notifications" 
            className="cursor-pointer group flex items-center justify-between rounded-lg px-3 py-2.5 transition-all duration-200 hover:bg-emerald-soft/50 hover:translate-x-1"
          >
            <div className="flex items-center gap-3 flex-1">
              <div className="p-1.5 rounded-lg bg-emerald-soft/50 group-hover:bg-emerald-subtle transition-colors duration-200 relative">
                <Bell className="h-4 w-4 text-green-night group-hover:scale-110 transition-transform duration-200" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-green-night rounded-full border border-white" />
                )}
              </div>
              <span className="font-medium text-sm text-text-primary">{t('nav.notifications')}</span>
          </div>
            <div className="flex items-center gap-2">
          {unreadCount > 0 && (
                <Badge className="bg-green-night text-white text-xs px-2 py-0.5 shadow-sm">
              {unreadCount > 99 ? "99+" : unreadCount}
            </Badge>
          )}
              <ChevronRight className="h-4 w-4 text-text-tertiary opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
            </div>
        </Link>
      </DropdownMenuItem>

      {isRenter && (
        <DropdownMenuItem asChild>
          <Link 
            to="/dashboard/renter/cancellations" 
            className="cursor-pointer group flex items-center justify-between rounded-lg px-3 py-2.5 transition-all duration-200 hover:bg-emerald-soft/50 hover:translate-x-1"
          >
            <div className="flex items-center gap-3">
              <div className="p-1.5 rounded-lg bg-emerald-soft/50 group-hover:bg-emerald-subtle transition-colors duration-200">
                <XCircle className="h-4 w-4 text-green-night group-hover:scale-110 transition-transform duration-200" />
              </div>
              <span className="font-medium text-sm text-text-primary">{t('nav.myCancellations')}</span>
            </div>
            <ChevronRight className="h-4 w-4 text-text-tertiary opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
          </Link>
        </DropdownMenuItem>
      )}

      </div>

      <DropdownMenuSeparator className="my-2 bg-gradient-to-r from-transparent via-border to-transparent" />
      
      {/* Bouton déconnexion avec style distinct - Utilise destructive (rouge) */}
      <DropdownMenuItem 
        onClick={onSignOut} 
        className="cursor-pointer group flex items-center gap-3 rounded-lg px-3 py-2.5 transition-all duration-200 hover:bg-destructive/10 hover:translate-x-1 mt-1"
      >
        <div className="p-1.5 rounded-lg bg-destructive/10 group-hover:bg-destructive/20 transition-colors duration-200">
          <LogOut className="h-4 w-4 text-destructive group-hover:scale-110 transition-transform duration-200" />
        </div>
        <span className="font-medium text-sm text-destructive">{t('common.logout')}</span>
      </DropdownMenuItem>
    </>
  );
};
